import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import axios from 'axios';
import i18next from 'i18next';
import { generatePath } from 'react-router-dom';
import { BaseError } from '~/components/Error/BaseError';

export type LanErrorResponse = {
  error: {
    errorCode: string;
    errorMessage: string;
    timestamp: number;
  };
};

export type HelpArticleModel = {
  id: number;
  title: string;
  body: string;
  category: boolean;
  recommend: boolean;
  recommendClose: number;
  satisStat: string;
  sortOrder: number;
  close: number;
};

export type HelpCategoryRawModel = {
  category: boolean;
  categoryId: number;
  children: (HelpCategoryRawModel | HelpArticleModel)[];
  customerSurveyStatus: string;
  depth: number;
  noticeStatus: string;
  shortcutStatus: string;
  socialPluginStatus: string;
  sortOrder: number;
  title: string;
};

export type HelpCategoryModel = Omit<HelpCategoryRawModel, 'children'> & {
  items: (HelpCategoryModel | HelpArticleModel)[];
};

export type HelpCategoryResponse = {
  categoryDisplayDepthType: string;
  lang: string;
  newHelp: boolean;
  pageDirection: string;
  supportLangs: string[];
  supportPlatforms: string[];
  helpComponent: HelpCategoryRawModel;
};

const LAN_URL = import.meta.env.VITE_LAN_URL;
const LAN_SERVICE_ID = import.meta.env.VITE_LAN_SERVICE_ID;
export const helpCategoriesUrl = `${LAN_URL}/v1/${LAN_SERVICE_ID}/web/help`;
export const helpCategoryUrl = `${LAN_URL}/v1/${LAN_SERVICE_ID}/web/help/category/:id`;

const refineCategory = (
  category: HelpCategoryRawModel | HelpArticleModel
): HelpCategoryModel | HelpArticleModel => {
  if (category.category) {
    return {
      ...category,
      items: (category as HelpCategoryRawModel).children.map(refineCategory),
    };
  } else {
    return category as HelpArticleModel;
  }
};

export const useHelpCategories = (options?: UseQueryOptions<HelpCategoryModel[]>) =>
  useQuery<HelpCategoryModel[]>(
    ['helpCategory'],
    async () => {
      const { data } = await axios
        .get<{ result: HelpCategoryResponse }>(helpCategoriesUrl, {
          params: {
            lang: i18next.resolvedLanguage,
            depth: 1,
          },
        })
        .catch((e) => {
          throw new BaseError('NOT_FOUND', e.payload);
        });
      return data.result.helpComponent.children.map(refineCategory) as HelpCategoryModel[];
    },
    options
  );
export const useHelpCategory = (id: string, options?: UseQueryOptions<HelpCategoryModel>) => {
  const url = generatePath(helpCategoryUrl, { id });

  return useQuery<HelpCategoryModel>(
    ['helpCategory', id],
    async () => {
      const { data } = await axios
        .get<{ result: HelpCategoryResponse }>(url, {
          params: {
            lang: i18next.language,
            depth: 2,
          },
        })
        .catch((e) => {
          throw new BaseError('NOT_FOUND', e.payload);
        });
      return {
        ...data.result.helpComponent,
        items: data.result.helpComponent.children.map(refineCategory),
      };
    },
    options
  );
};
